/**
 * Утилиты для расчета метрик аналитики
 * Содержит чистые функции для вычислений без side effects
 */

export interface RevenueComponents {
  new_sales_amount?: number
  renewals_amount?: number
  returning_clients_amount?: number
  upgrades_amount?: number
}

/**
 * Рассчитывает общую выручку из компонентов
 */
export function calculateTotalRevenue(components: RevenueComponents): number {
  return (
    (components.new_sales_amount || 0) +
    (components.renewals_amount || 0) +
    (components.returning_clients_amount || 0) +
    (components.upgrades_amount || 0)
  )
}

/**
 * Рассчитывает средний чек
 */
export function calculateAverageSale(revenue: number, salesCount: number): number {
  if (salesCount === 0) return 0
  return revenue / salesCount
}

/**
 * Рассчитывает конверсию в процентах
 */
export function calculateConversion(numerator: number, denominator: number): number {
  if (denominator === 0) return 0
  return (numerator / denominator) * 100
}

/**
 * Рассчитывает среднюю недельную метрику на основе дневных данных
 */
export function calculateWeeklyAverage(totalValue: number, daysCount: number): number {
  if (daysCount === 0) return 0
  return (totalValue / daysCount) * 7
}

/**
 * Рассчитывает недельный план на основе плана периода
 */
export function calculateWeeklyPlan(
  periodPlan: number,
  weeksInPeriod: number
): number {
  if (weeksInPeriod === 0) return 0
  return periodPlan / weeksInPeriod
}

/**
 * Рассчитывает дневную цифру (сколько нужно делать в день для достижения плана)
 */
export function calculateDailyTarget(
  planValue: number,
  factValue: number,
  daysRemaining: number
): number {
  if (daysRemaining === 0) return 0
  const remaining = planValue - factValue
  return Math.max(0, remaining / daysRemaining)
}

/**
 * Рассчитывает отклонение от плана в процентах
 */
export function calculatePlanDeviation(factValue: number, planValue: number): number {
  if (planValue === 0) return 0
  return ((factValue - planValue) / planValue) * 100
}

/**
 * Определяет количество дней между датами (включительно)
 */
export function calculateDaysBetween(startDate: Date, endDate: Date): number {
  const diffTime = endDate.getTime() - startDate.getTime()
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
}

/**
 * Рассчитывает количество дней до конца периода (включая сегодня)
 * @param endDateString - дата окончания в формате 'YYYY-MM-DD'
 * @param todayString - текущая дата в формате 'YYYY-MM-DD' (опционально)
 */
export function calculateDaysRemaining(endDateString: string, todayString?: string): number {
  const today = todayString 
    ? new Date(todayString + 'T00:00:00')
    : new Date()
  
  // Приводим today к формату YYYY-MM-DD без времени
  const todayStr = todayString || `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  
  const endDate = new Date(endDateString + 'T00:00:00')
  const todayDate = new Date(todayStr + 'T00:00:00')
  
  const diffTime = endDate.getTime() - todayDate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  
  // +1 потому что включаем сегодняшний день
  return Math.max(0, diffDays + 1)
}

/**
 * Группирует дневные данные по неделям
 */
export interface DailyData {
  date: string
  [key: string]: any
}

export interface WeekData {
  weekNumber: number
  weekStart: string
  weekEnd: string
  data: DailyData[]
}

export function groupByWeeks(dailyData: DailyData[], periodStart: string): WeekData[] {
  const weeks: WeekData[] = []
  const periodStartDate = new Date(periodStart)
  
  dailyData.forEach(day => {
    const dayDate = new Date(day.date)
    const daysSinceStart = Math.floor(
      (dayDate.getTime() - periodStartDate.getTime()) / (1000 * 60 * 60 * 24)
    )
    const weekNumber = Math.floor(daysSinceStart / 7)
    
    let week = weeks.find(w => w.weekNumber === weekNumber)
    if (!week) {
      const weekStartDate = new Date(periodStartDate)
      weekStartDate.setDate(periodStartDate.getDate() + weekNumber * 7)
      const weekEndDate = new Date(weekStartDate)
      weekEndDate.setDate(weekStartDate.getDate() + 6)
      
      week = {
        weekNumber,
        weekStart: weekStartDate.toISOString().split('T')[0],
        weekEnd: weekEndDate.toISOString().split('T')[0],
        data: []
      }
      weeks.push(week)
    }
    
    week.data.push(day)
  })
  
  return weeks.sort((a, b) => a.weekNumber - b.weekNumber)
}

/**
 * Суммирует значения по ключу из массива объектов
 */
export function sumByKey<T>(data: T[], key: keyof T): number {
  return data.reduce((sum, item) => {
    const value = item[key]
    return sum + (typeof value === 'number' ? value : 0)
  }, 0)
}

/**
 * Рассчитывает ROMI (Return on Marketing Investment)
 * ROMI = (Выручка - Затраты на маркетинг) / Затраты на маркетинг * 100%
 */
export function calculateROMI(revenue: number, marketingBudget: number): number {
  if (marketingBudget === 0) return 0
  return ((revenue - marketingBudget) / marketingBudget) * 100
}

/**
 * Рассчитывает стоимость привлечения (CAC)
 */
export function calculateCAC(marketingBudget: number, acquisitionsCount: number): number {
  if (acquisitionsCount === 0) return 0
  return marketingBudget / acquisitionsCount
}

/**
 * Определяет цвет для значения конверсии относительно плана
 */
export function getConversionColor(factCr: number, planCr: number): string {
  if (planCr === 0) return 'text-gray-700'
  return factCr >= planCr ? 'text-green-600' : 'text-red-600'
}

/**
 * Форматирует диапазон дат для отображения
 */
export function formatDateRange(startDate: Date | string, endDate: Date | string): string {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'short' 
    })
  }
  
  return `${formatDate(start)} - ${formatDate(end)}`
}
